import React, {
  useContext,
  createContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { Dispatch, SetStateAction } from 'react';

import useGetAvailableSingleCallJobs from 'state/remote/remote-freelancer/use-get-available-single-call-jobs';

import useGetAcceptedJobsForFreelancer from 'state/remote/remote-freelancer/use-get-accepted-jobs-for-freelancer';

import useGetOrganisationNames from 'state/remote/remote-freelancer/use-get-organisation-names';

import type { AvailableSingleCallJob } from 'state/remote/types';

import client from 'state/remote/client';

import type {
  useGetOrganisationNamesType,
  OrganisationNameType,
} from 'state/remote/remote-freelancer/use-get-organisation-names';

type FreelancerAvailableJobs = {
  data: [string, AvailableSingleCallJob[]][];
  isLoading?: boolean;
  error: Error | null;
  personalInvitesNumber: number;
  refetch: (options: {
    throwOnError?: boolean;
    cancelRefetch?: boolean;
  }) => unknown;
};

type FreelancerAcceptedJobs = {
  data: AvailableSingleCallJob[];
  isLoading?: boolean;
  error: Error | null;
};

type FreelancerJobsContext = {
  availablejobs: FreelancerAvailableJobs;
  acceptedJobs: FreelancerAcceptedJobs;
  closeFilter: boolean;
  organisationSuggestions: useGetOrganisationNamesType;
  alertMessage: string;
  filterCompetences: string[];
  filterEnabled: boolean;
  filterInitiated: boolean;
  handleFilterEnabled: (arg: boolean) => void;
  organisationName: OrganisationNameType;
  setAlertMessage: Dispatch<SetStateAction<string>>;
  setFilterCompetences: Dispatch<SetStateAction<string[]>>;
  setFilterInitiated: Dispatch<SetStateAction<boolean>>;
  setMinimumPay: Dispatch<SetStateAction<string>>;
  setIsWholeDay: Dispatch<SetStateAction<boolean>>;
  setIfAvailable: Dispatch<SetStateAction<boolean>>;
  setDistance: Dispatch<SetStateAction<string>>;
  setCloseFilter: (status: boolean) => void;
  setIsNewJob: Dispatch<SetStateAction<boolean>>;
  setKindOfJob: Dispatch<SetStateAction<string>>;
  setOrganisationName: Dispatch<SetStateAction<OrganisationNameType>>;
  setPostCode: Dispatch<SetStateAction<string | undefined>>;
  setWarningMessage: Dispatch<SetStateAction<string>>;
  warningMessage: string;
  minimumPay: string;
  isWholeDay: boolean;
  ifAvailable: boolean;
  distance: string;
  isNewJob: boolean;
  kindOfJob: string;
  postCode: string | undefined;
};

const FreelancerAvailaleJobsInitialValue = {
  acceptedJobs: {
    data: [] as AvailableSingleCallJob[],
    error: null,
    isLoading: false,
  },
  alertMessage: '',
  availablejobs: {
    data: [] as [string, AvailableSingleCallJob[]][],
    error: null,
    personalInvitesNumber: 0,
    refetch: () => {},
  },
  closeFilter: false,
  distance: '',
  filterBtnClick: false,
  filterCompetences: [],
  filterEnabled: false,
  filterInitiated: false,
  handleFilterEnabled: () => {},
  ifAvailable: false,
  isNewJob: false,
  isWholeDay: false,
  kindOfJob: 'any',
  minimumPay: '',
  organisationName: {
    text: '',
    value: undefined,
  },
  organisationSuggestions: {
    data: [] as OrganisationNameType[],
    error: null,
    isLoading: false,
  },
  postCode: '',
  setAlertMessage: () => '',
  setCloseFilter: () => false,
  setDistance: () => '',
  setFilterCompetences: () => [],
  setFilterInitiated: () => false,
  setIfAvailable: () => false,
  setIsNewJob: () => false,
  setIsWholeDay: () => false,
  setKindOfJob: () => '',
  setMinimumPay: () => 0,
  setOrganisationName: () => {},
  setPostCode: () => '',
  setWarningMessage: () => '',
  warningMessage: '',
};

const FreelancerAvailaleJobsContext = createContext<FreelancerJobsContext>(
  FreelancerAvailaleJobsInitialValue,
);

const FreelancerAvailaleJobsContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [alertMessage, setAlertMessage] = useState('');
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [minimumPay, setMinimumPay] = useState('');
  const [isWholeDay, setIsWholeDay] = useState(false);
  const [distance, setDistance] = useState('');
  const [isNewJob, setIsNewJob] = useState(false);
  const [ifAvailable, setIfAvailable] = useState(false);
  const [kindOfJob, setKindOfJob] = useState('any');
  const [filterInitiated, setFilterInitiated] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [postCode, setPostCode] = useState<string | undefined>(undefined);
  const [organisationName, setOrganisationName] =
    useState<OrganisationNameType>({
      text: '',
      value: undefined,
    });
  const [filterCompetences, setFilterCompetences] = useState<string[]>([]);
  const [closeFilter, setCloseFilter] = useState<boolean>(false);

  const organisationSuggestions = useGetOrganisationNames(
    organisationName?.text,
    280, // debounce param
  );

  const availablejobs = useGetAvailableSingleCallJobs(
    filterEnabled,
    minimumPay,
    isWholeDay,
    distance,
    isNewJob,
    ifAvailable,
    kindOfJob,
    Number(organisationName.value) || undefined,
    filterCompetences,
    filterInitiated,
    setWarningMessage,
    postCode,
  );

  const availableJobsContext = useMemo((): FreelancerAvailableJobs => {
    return availablejobs;
  }, [availablejobs]);

  const acceptedJobs = useGetAcceptedJobsForFreelancer();

  const acceptedJobsContext = useMemo((): FreelancerAcceptedJobs => {
    return acceptedJobs;
  }, [acceptedJobs]);

  const handleFilterEnabled = (enable: boolean) => {
    setFilterEnabled(enable);
    setCloseFilter(true);
    setWarningMessage('');

    if (enable) {
      setFilterInitiated(true);
      const filter = {
        competences: filterCompetences,
        if_available: ifAvailable,
        isnew: isNewJob,
        kind: kindOfJob,
        min_rate: minimumPay,
        organisation: organisationName,
        post_code: postCode,
        travel_distance: distance,
        whole_days: isWholeDay,
      };
      localStorage.setItem('filter', JSON.stringify(filter));
    } else {
      if (filterEnabled) {
        client.removeQueries('get-available-single-call-jobs');
      }
      localStorage.removeItem('filter');
      setMinimumPay('');
      setIsWholeDay(false);
      setDistance('');
      setIfAvailable(false);
      setIsNewJob(false);
      setKindOfJob('any');
      setFilterInitiated(false);
      setPostCode(undefined);
      setOrganisationName({
        text: '',
        value: undefined,
      });
      setFilterCompetences([]);
    }
  };

  const filters = localStorage.getItem('filter');
  if (!filterInitiated && filters) {
    setFilterInitiated(true);
    setFilterEnabled(true);
    const filterObj = JSON.parse(filters);
    setMinimumPay(filterObj.min_rate || '');
    setIsWholeDay(filterObj.whole_days || false);
    setIfAvailable(
      filterObj?.if_available === undefined ? false : filterObj?.if_available,
    );
    setDistance(filterObj.travel_distance || '');
    setIsNewJob(filterObj.isnew || false);
    if (filterObj.kind === 'choice') setKindOfJob('urgent' || 'any');
    else setKindOfJob(filterObj.kind || 'any');
    setKindOfJob(filterObj.kind || 'any');
    setPostCode(filterObj.post_code || undefined);
    setOrganisationName(
      filterObj.organisation || {
        text: '',
        value: undefined,
      },
    );
    setFilterCompetences(filterObj.competences || []);
  }

  const setFilterClosed = useCallback(
    (status: boolean) => {
      setCloseFilter(status);
    },
    [setCloseFilter],
  );

  return (
    <FreelancerAvailaleJobsContext.Provider
      value={{
        acceptedJobs: acceptedJobsContext,
        alertMessage,
        availablejobs: availableJobsContext,
        closeFilter,
        distance,
        filterCompetences,
        filterEnabled,
        filterInitiated,
        handleFilterEnabled,
        ifAvailable,
        isNewJob,
        isWholeDay,
        kindOfJob,
        minimumPay,
        organisationName,
        organisationSuggestions,
        postCode,
        setAlertMessage,
        setCloseFilter: setFilterClosed,
        setDistance,
        setFilterCompetences,
        setFilterInitiated,
        setIfAvailable,
        setIsNewJob,
        setIsWholeDay,
        setKindOfJob,
        setMinimumPay,
        setOrganisationName,
        setPostCode,
        setWarningMessage,
        warningMessage,
      }}
    >
      {children}
    </FreelancerAvailaleJobsContext.Provider>
  );
};

export const useAvailableSingleCallJobs = (): FreelancerJobsContext =>
  useContext(FreelancerAvailaleJobsContext);

export default {
  Consumer: FreelancerAvailaleJobsContext.Consumer,
  Provider: FreelancerAvailaleJobsContextProvider,
};
